import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["micButton", "modeIndicator", "levelRing", "micIcon", "stopIcon"]
  static values = {
    state: { type: String, default: "idle" },
    mode: { type: String, default: "push" },
    supported: { type: Boolean, default: false }
  }

  connect() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      this.supportedValue = false
      return
    }

    this.supportedValue = true
    this.recognition = new SpeechRecognition()
    this.recognition.continuous = true
    this.recognition.interimResults = true
    this.recognition.lang = "en-US"

    this.recognition.onresult = (e) => this.handleResult(e)
    this.recognition.onspeechend = () => this.handleSpeechEnd()
    this.recognition.onend = () => this.handleEnd()
    this.recognition.onerror = (e) => this.handleError(e)

    this.transcript = ""
    this.finalTranscript = ""
    this.longPressTimer = null
    this.longPressTriggered = false
    this.audioStream = null
    this.analyser = null
    this.audioCtx = null
    this.levelAnimFrame = null
    this.levelData = null
    this.stopping = false
    this.ttsPaused = false // true when mic is paused for TTS playback
    this._pointerIsDown = false

    // Listen for TTS start/end to pause mic during playback
    this.boundTtsStart = () => this.pauseForTts()
    this.boundTtsEnd = () => this.resumeAfterTts()
    document.addEventListener("tts:start", this.boundTtsStart)
    document.addEventListener("tts:end", this.boundTtsEnd)
  }

  disconnect() {
    this.stopListening()
    this.stopAudioLevel()
    document.removeEventListener("tts:start", this.boundTtsStart)
    document.removeEventListener("tts:end", this.boundTtsEnd)
  }

  // --- Pointer events for tap / long-press ---

  micDown(event) {
    event.preventDefault()
    this._pointerIsDown = true

    if (this.stateValue === "listening") {
      this._stoppedByTap = true
      this.stopListening()
      return
    }

    if (this.stateValue !== "idle") return

    this._stoppedByTap = false
    this.longPressTriggered = false
    this.longPressTimer = setTimeout(() => {
      this.longPressTriggered = true
      this.modeValue = "dictation"
      this.startListening()
      if (navigator.vibrate) navigator.vibrate(50)
    }, 500)
  }

  micUp(event) {
    if (!this._pointerIsDown) return
    this._pointerIsDown = false
    clearTimeout(this.longPressTimer)

    if (this.longPressTriggered) return
    if (this._stoppedByTap) return
    if (this.stateValue === "listening") return

    if (this.stateValue === "idle") {
      this.modeValue = "push"
      this.startListening()
    }
  }

  // --- Core lifecycle ---

  startListening() {
    if (this.stateValue === "listening") return

    // Interrupt any active TTS (barge-in)
    this.dispatch("interrupt", { bubbles: true })

    // Trigger listening animation on avatar
    const avatarEl = document.querySelector("[data-controller='avatar']")
    const controller = avatarEl?.avatarController
    if (controller) controller.playAnimation("__idle")

    this.transcript = ""
    this.finalTranscript = ""
    this._lastWrittenValue = undefined
    this.stopping = false
    this.stateValue = "listening"

    // Prime the mic permission via getUserMedia first so SpeechRecognition
    // reuses the grant instead of firing a second prompt. Both APIs ask for
    // the same permission but the browser treats them as separate requests
    // if SpeechRecognition.start() runs first.
    this.startAudioLevel().finally(() => {
      try {
        this.recognition.start()
      } catch (e) {
        // Already started
        this.stateValue = "idle"
      }
    })
  }

  stopListening() {
    if (this.stateValue !== "listening") return
    this.stopping = true

    try {
      this.recognition.stop()
    } catch (e) {
      // Not started
    }
  }

  // --- SpeechRecognition handlers ---

  handleResult(event) {
    let interim = ""
    let finalChunk = ""

    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i]
      if (result.isFinal) {
        finalChunk += result[0].transcript
      } else {
        interim += result[0].transcript
      }
    }

    const chatInput = document.querySelector("[data-chat-target='input']")

    // Rebase on manual edits: if the user has changed (or cleared) the
    // input since we last wrote to it, trust their value over our buffer.
    // Without this, deleting the text mid-session or between sessions
    // leaves the old transcript in finalTranscript, and the next chunk
    // gets concatenated onto the ghost text.
    if (chatInput && this._lastWrittenValue !== undefined &&
        chatInput.value !== this._lastWrittenValue) {
      this.finalTranscript = chatInput.value
    }

    this.finalTranscript += finalChunk
    const nextValue = this.finalTranscript + interim
    if (chatInput) {
      chatInput.value = nextValue
      this._lastWrittenValue = nextValue
      chatInput.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }

  handleSpeechEnd() {
    if (this.modeValue === "push") {
      this.stopListening()
    }
  }

  handleEnd() {
    // If paused for TTS, don't finalize — we'll restart after TTS ends
    if (this.ttsPaused) return

    this.stopAudioLevel()

    // Restore avatar to idle
    const avatarEl = document.querySelector("[data-controller='avatar']")
    const controller = avatarEl?.avatarController
    if (controller) controller.playAnimation("__idle")

    const chatInput = document.querySelector("[data-chat-target='input']")
    const text = chatInput?.value?.trim()

    if (this.modeValue === "push" && text) {
      // Push-to-talk: auto-send what's in the input
      this.stateValue = "processing"
      this.dispatch("transcript", {
        detail: { text, autoSend: true },
        bubbles: true
      })
      if (chatInput) chatInput.value = ""
    } else if (this.modeValue === "dictation") {
      // Dictation: leave text in input, focus for editing/sending
      if (chatInput) chatInput.focus()
    }

    this.stateValue = "idle"
  }

  handleError(event) {
    this.stopAudioLevel()

    if (event.error === "not-allowed") {
      // Flash mic red briefly
      if (this.hasMicButtonTarget) {
        this.micButtonTarget.dataset.voiceError = "true"
        setTimeout(() => { this.micButtonTarget.dataset.voiceError = "false" }, 2000)
      }
    }

    this.stateValue = "idle"
  }

  // --- Audio level visualization ---

  async startAudioLevel() {
    try {
      this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (!this.audioCtx || this.audioCtx.state === 'closed') {
        this.audioCtx = new AudioContext()
      }
      const source = this.audioCtx.createMediaStreamSource(this.audioStream)
      this.analyser = this.audioCtx.createAnalyser()
      this.analyser.fftSize = 256
      source.connect(this.analyser)
      this.levelData = new Uint8Array(this.analyser.frequencyBinCount)
      this.updateLevel()
    } catch {
      // getUserMedia denied — fall back to CSS-only pulse
    }
  }

  updateLevel() {
    if (this.stateValue !== "listening") return

    this.analyser.getByteFrequencyData(this.levelData)
    const rms = Math.sqrt(
      this.levelData.reduce((sum, v) => sum + v * v, 0) / this.levelData.length
    ) / 255

    if (this.hasMicButtonTarget) {
      this.micButtonTarget.style.setProperty("--voice-level", rms.toFixed(3))
    }

    this.levelAnimFrame = requestAnimationFrame(() => this.updateLevel())
  }

  stopAudioLevel() {
    cancelAnimationFrame(this.levelAnimFrame)
    this.audioStream?.getTracks().forEach(t => t.stop())
    if (this.audioCtx?.state !== "closed") {
      this.audioCtx?.close().catch(() => {})
    }
    this.audioStream = null
    this.audioCtx = null
    this.analyser = null
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.style.setProperty("--voice-level", "0")
    }
  }

  // --- TTS pause/resume (prevents feedback loop) ---

  pauseForTts() {
    if (this.stateValue !== "listening") return
    this.ttsPaused = true
    try {
      this.recognition.stop()
    } catch {}
    this.stopAudioLevel()
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.dataset.voiceState = "paused"
    }
    this.updateModeLabel("PAUSED — ASSISTANT SPEAKING")
  }

  resumeAfterTts() {
    if (!this.ttsPaused) return
    this.ttsPaused = false

    // Small delay to let speaker audio dissipate before restarting mic
    setTimeout(() => {
      if (this.stateValue === "listening" || this.micButtonTarget?.dataset.voiceState === "paused") {
        this.stateValue = "listening"
        try {
          this.recognition.start()
        } catch {}
        this.startAudioLevel()
        this.updateModeLabel(this.modeValue === "dictation" ? "DICTATION" : "LISTENING")
      }
    }, 400)
  }

  // --- Stimulus value callbacks ---

  stateValueChanged(state) {
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.dataset.voiceState = state
    }
    // Toggle mic/stop icons
    if (this.hasMicIconTarget && this.hasStopIconTarget) {
      const listening = state === "listening" || state === "paused"
      this.micIconTarget.classList.toggle("hidden", listening)
      this.stopIconTarget.classList.toggle("hidden", !listening)
    }
    // Update mode label
    if (state === "listening") {
      this.updateModeLabel(this.modeValue === "dictation" ? "DICTATION" : "LISTENING")
    } else if (state === "processing") {
      this.updateModeLabel("PROCESSING...")
    } else if (state === "idle") {
      this.updateModeLabel("")
    }
  }

  supportedValueChanged(supported) {
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.dataset.voiceSupported = String(supported)
    }
  }

  modeValueChanged(mode) {
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.dataset.voiceMode = mode
    }
    if (this.stateValue === "listening") {
      this.updateModeLabel(mode === "dictation" ? "DICTATION" : "LISTENING")
    }
  }

  updateModeLabel(text) {
    if (this.hasModeIndicatorTarget) {
      this.modeIndicatorTarget.textContent = text
    }
    // Also update the inline indicator near the mic button
    const inlineIndicator = this.element.querySelector("[data-voice-target='modeIndicator']:last-of-type")
    if (inlineIndicator && inlineIndicator !== this.modeIndicatorTarget) {
      inlineIndicator.textContent = text
    }
  }
}
